import { useState } from 'react'
import { Plus } from 'lucide-react'
import { EmptyRow, ErpTable, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { fmtDateTime } from '@/shared/lib/format'
import type { SurveyAssignmentDto } from '@/shared/api/types'
import { listAttempts } from './api'
import { AttemptDialog, outcomeLabel, outcomeTone } from './AttemptDialog'
import { Pill } from './vocab'

/**
 * Every approach made under one assignment, in the order they were made.
 *
 * ## Why this is a list and not a count
 *
 * "Three attempts" and "not home, not home, refused" are different facts, and the
 * second is the one a supervisor can act on. The AAPOR rates on the disposition
 * panel are this table summed; showing only the sum leaves nobody able to check
 * it, or to see that an interviewer has logged eleven non-contacts in one morning.
 *
 * `attemptNo` is assigned server-side as the previous highest plus one, so the
 * sequence is the server's and this component never renumbers.
 */
export function AttemptsPanel({
  surveyId, assignment, canRecord, reloadToken, onRecorded,
}: {
  surveyId: string
  assignment: SurveyAssignmentDto
  /** `survey:field`. False renders the list without the write affordance. */
  canRecord: boolean
  /**
   * Bumped by a parent that recorded an attempt through its own dialog, so the
   * list under the card refreshes with it rather than going stale behind a
   * "Record an attempt" button that lives somewhere else.
   */
  reloadToken?: number
  onRecorded?: () => void
}) {
  const [recording, setRecording] = useState(false)
  const attempts = useLoad(
    () => listAttempts(surveyId, assignment.id), [surveyId, assignment.id, reloadToken])

  const rows = attempts.data ?? []
  const revoked = assignment.statusCode === 'REVOKED'

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-erp-xs uppercase tracking-wide text-muted-foreground">
          Contact attempts
        </span>
        {canRecord && (
          <button type="button" className="erp-btn h-7 text-erp-xs"
            onClick={() => setRecording(true)}>
            <Plus className="h-3 w-3" /> Record an attempt
          </button>
        )}
      </div>

      <ErrorStrip message={attempts.error} />

      {attempts.loading && rows.length === 0 ? (
        <LoadingBlock label="Loading attempts…" />
      ) : (
        <ErpTable head={(
          <>
            <Th className="text-right">#</Th>
            <Th>Outcome</Th>
            <Th>When</Th>
            <Th>Note</Th>
          </>
        )}>
          {rows.length === 0 ? (
            <EmptyRow colSpan={4} message={
              'No attempts recorded. Interviews collected without attempts behind them give a '
              + 'collected count and no response rate — the denominators have nothing in them.'
            } />
          ) : rows.map((a) => (
            <tr key={a.id} className="hover:bg-muted/40">
              <Td className="text-right tabular-nums text-muted-foreground">{a.attemptNo}</Td>
              <Td><Pill tone={outcomeTone(a.outcomeCode)}>{outcomeLabel(a.outcomeCode)}</Pill></Td>
              <Td className="text-muted-foreground whitespace-nowrap">
                {fmtDateTime(a.attemptedAt)}
              </Td>
              <Td className="text-muted-foreground">{a.note ?? '—'}</Td>
            </tr>
          ))}
        </ErpTable>
      )}

      {revoked && rows.length > 0 && (
        <Note tone="info">
          This assignment was revoked and these attempts were kept. That is deliberate: they are
          what was actually tried, and deleting them would silently raise the poll's response
          rate by shrinking its denominator.
        </Note>
      )}

      {recording && (
        <AttemptDialog
          open
          surveyId={surveyId}
          assignment={assignment}
          onClose={() => setRecording(false)}
          onSaved={() => {
            setRecording(false)
            attempts.reload()
            // The parent owns the disposition rates and the collected counts, both
            // of which this attempt just moved.
            onRecorded?.()
          }}
        />
      )}
    </div>
  )
}
